import Link from "next/link"
import Image from "next/image"
import { ArrowRight } from "lucide-react"
import type { Author } from "@/lib/types/author"

interface AuthorBioCardProps {
  author: Author
}

export function AuthorBioCard({ author }: AuthorBioCardProps) {
  const bioText =
    author.bio?.short ||
    `${author.shortCredential}. Read all articles by ${author.name} on their author page.`

  const specializations = author.specializations?.slice(0, 4) ?? []
  const authorHref = `/blog/author/${author.slug}`

  return (
    <aside
      aria-labelledby={`author-bio-${author.slug}-heading`}
      className="ds-glass rounded-[20px] p-5 md:p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1"
    >
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
        <Link
          href={authorHref}
          prefetch={false}
          className="shrink-0 self-start"
          aria-label={`View all articles by ${author.name}`}
        >
          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-white/70 shadow-[0_12px_28px_-12px_rgba(10,28,18,0.45)]">
            <Image
              src={author.headshot.url}
              alt={author.headshot.alt}
              fill
              className="object-cover"
              sizes="96px"
              loading="lazy"
            />
          </div>
        </Link>

        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">
            About the author
          </p>
          <h3
            id={`author-bio-${author.slug}-heading`}
            className="font-editorial text-xl font-bold text-gray-900 mb-1"
          >
            <Link
              href={authorHref}
              prefetch={false}
              className="hover:text-green-700 transition-colors"
            >
              {author.name}
            </Link>
          </h3>
          <p className="text-sm font-semibold text-green-700 mb-3">{author.shortCredential}</p>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-4">
            {bioText}
          </p>

          {specializations.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {specializations.map((spec) => (
                <span
                  key={spec}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/55 backdrop-blur-md text-green-800 border border-white/55"
                >
                  {spec}
                </span>
              ))}
            </div>
          )}

          <Link
            href={authorHref}
            prefetch={false}
            className="inline-flex items-center gap-1 text-sm font-semibold text-green-700 hover:text-green-800 transition-colors"
          >
            View all articles by {author.name}
            <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </aside>
  )
}
